import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from './icons';

/**
 * The sidebar's parts for smart lists and saved filters (migration 074),
 * drawn by TodoSidebar once the server has them:
 *
 *   FoldHead        a group's heading that folds it away - Favourites and
 *                   My lists. Each person folds what they do not use, and
 *                   it stays folded (on this device: a convenience, not a
 *                   setting).
 *
 * Oct 2026: "Customise views" and the Filters group are gone from the
 * sidebar. The six fixed entries, More and the ☆ that pins to Favourites
 * (todoNav.js) replaced them; pinned filters sit in Favourites.
 */

const foldKey = (who) => `tda-side-folded:${who || ''}`;
const readFolds = (who) => {
  try {
    const v = JSON.parse(localStorage.getItem(foldKey(who)) || '[]');
    return new Set(Array.isArray(v) ? v : []);
  } catch (err) {
    return new Set();
  }
};

/** Which of the sidebar's groups are folded, remembered per person on this device. */
export function useFolds(who) {
  const [folded, setFolded] = useState(() => readFolds(who));
  // Who it is is known a moment after the page opens: read theirs then.
  useEffect(() => { setFolded(readFolds(who)); }, [who]);
  const toggle = useCallback((group) => setFolded((prev) => {
    const next = new Set(prev);
    if (next.has(group)) next.delete(group); else next.add(group);
    try { localStorage.setItem(foldKey(who), JSON.stringify([...next])); } catch (err) { /* not kept */ }
    return next;
  }), [who]);
  return { isFolded: (g) => folded.has(g), toggle };
}

/** A group's heading: the name folds it; anything else (an add button) sits beside. */
export function FoldHead({ group, title, folds, controls, children }) {
  const shut = folds.isFolded(group);
  return (
    <div className={`tda-side-head tdf-fold-head${shut ? ' is-folded' : ''}`} data-fold={group}>
      <h3>
        <button type="button" className="tdf-fold" aria-expanded={!shut} aria-controls={controls}
                onClick={() => folds.toggle(group)}>
          <Icon name="chevronDown" size={12} className="tdf-fold-ic" />
          <span>{title}</span>
        </button>
      </h3>
      {children}
    </div>
  );
}
